import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { formatAuditTime } from '../services/auditTime';
import { apiMessage, listAuditRuns } from '../services/jobAuditApi';
import TechnicianScoreGauge from './TechnicianScoreGauge';
import HeaderHelp from './HeaderHelp';
import { serviceTitanJobUrl } from './serviceTitanJob';

const PAGE_SIZE = 50;

const formatStatus = (status) => {
  if (!status) return '—';
  return status
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const statusTone = (status) => {
  if (status === 'critical_failure') return 'bg-red-100 text-red-800';
  if (status === 'human_review_required') return 'bg-purple-100 text-purple-800';
  if (status === 'technician_correction_required') return 'bg-amber-100 text-amber-800';
  if (status === 'accepted' || status === 'accepted_with_warnings') {
    return 'bg-green-100 text-green-800';
  }
  return 'bg-gray-100 text-gray-700';
};

const JobAuditTechnicianAudits = () => {
  const navigate = useNavigate();
  const { technicianId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenant = searchParams.get('tenant') || '';
  const name = searchParams.get('name') || '';
  const offset = Math.max(0, parseInt(searchParams.get('offset') || '0', 10) || 0);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, items: [] });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listAuditRuns({
      tenant,
      technician_id: technicianId || '',
      limit: PAGE_SIZE,
      offset,
    })
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(apiMessage(err, 'Failed to load technician audits'));
          setData({ total: 0, items: [] });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [technicianId, tenant, offset]);

  const showingFrom = data.total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + (data.items?.length || 0), data.total);
  const title = name || 'Technician';

  return (
    <div className="space-y-4">
      <div>
        <Link to="/audit/technicians" className="text-sm text-blue-700 hover:underline">
          ← Technicians
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-gray-900">{title}</h1>
        <p className="mt-1 text-sm text-gray-600">
          HHRC audits for this technician{tenant ? ` · ${tenant}` : ''}. Open a row for findings and review.
        </p>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="flex items-center justify-between border-b px-4 py-3 text-sm text-gray-600">
          <span>
            {loading
              ? 'Loading…'
              : `Showing ${showingFrom}–${showingTo} of ${data.total}`}
          </span>
          <div className="space-x-2">
            <button
              type="button"
              className="rounded border px-2 py-1 disabled:opacity-40"
              disabled={offset === 0 || loading}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                const prev = Math.max(0, offset - PAGE_SIZE);
                if (prev) next.set('offset', String(prev));
                else next.delete('offset');
                setSearchParams(next);
              }}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border px-2 py-1 disabled:opacity-40"
              disabled={offset + PAGE_SIZE >= data.total || loading}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.set('offset', String(offset + PAGE_SIZE));
                setSearchParams(next);
              }}
            >
              Next
            </button>
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Tenant</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Job</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                <span className="inline-flex items-center gap-1">
                  Score
                  <HeaderHelp id="technician-score-help" label="What the score means">
                    This job&apos;s Home Health Report Card score out of 100. 75 and above is good,
                    50 to 74 is ok, and 49 or below is bad.
                  </HeaderHelp>
                </span>
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Status</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Created (ET)</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                <span className="inline-flex items-center gap-1">
                  Issues
                  <HeaderHelp id="technician-issues-help" label="What issues means" align="right">
                    Checklist fields that failed, are missing, warned, or need review. Passes and
                    fields that do not apply are not included.
                  </HeaderHelp>
                </span>
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                <span className="inline-flex items-center gap-1">
                  Findings
                  <HeaderHelp id="technician-findings-help" label="What findings means" align="right">
                    Every checklist field the audit recorded a result for, including passes and
                    fields that do not apply.
                  </HeaderHelp>
                </span>
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                <span className="inline-flex items-center gap-1">
                  Percentage
                  <HeaderHelp
                    id="technician-percentage-help"
                    label="What percentage means"
                    align="right"
                  >
                    Issues divided by findings.
                  </HeaderHelp>
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {(data.items || []).map((row) => {
              const jobUrl = serviceTitanJobUrl(row.tenant_code, row.job_id);
              const issues = row.issues_count ?? 0;
              const findings = row.findings_count ?? 0;
              const issuePercent = findings > 0 ? `${Math.round((issues / findings) * 100)}%` : '—';
              return (
              <tr
                key={row.audit_run_id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/audit/${row.audit_run_id}`)}
              >
                <td className="whitespace-nowrap px-4 py-3 font-mono text-sm">{row.tenant_code}</td>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-sm">
                  {jobUrl ? (
                    <a
                      href={jobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.job_id}
                    </a>
                  ) : (
                    row.job_id
                  )}
                </td>
                <td className="px-4 py-2">
                  <TechnicianScoreGauge
                    score={typeof row.score === 'number' ? row.score : null}
                    labelPrefix={`Job ${row.job_id} score`}
                  />
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusTone(row.overall_job_status)}`}>
                    {formatStatus(row.overall_job_status)}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                  {formatAuditTime(row.created_at)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                  {issues}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                  {findings}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm font-bold text-gray-900">
                  {issuePercent}
                </td>
              </tr>
              );
            })}
            {!loading && (data.items || []).length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                  No scored audit runs for this technician.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobAuditTechnicianAudits;
